import React from 'react';

/**
 * Helper to bridge Formik with components designed for redux-form
 * Expects component to receive 'input' and 'meta' props.
 * 
 * Standalone component style:
 * <FormikFieldAdapter component={MyComp} name="myField" formik={formik} ... />
 */
export const FormikFieldAdapter = ({ component: Component, name, formik, type, value: radioValue, ...rest }) => {
    // getFieldProps returns { name, value, onChange, onBlur } 
    // or { name, value, onChange, onBlur, checked } if type and value are provided for radios/checkboxes
    const fieldProps = formik.getFieldProps({ name, type, value: radioValue });

    const input = {
        ...fieldProps,
        onChange: (x) => {
            // Check if value is a synthetic event or a direct value
            if (x && typeof x === 'object' && x.target !== undefined) {
                formik.handleChange(x);
            } else {
                formik.setFieldValue(name, x);
            }
        },
        onBlur: (e) => {
            // Support both event-based and manual blurs
            if (e && e.target) {
                formik.handleBlur(e);
            } else {
                formik.setFieldTouched(name, true);
            }
        },
    };

    const submitFailed = formik.submitCount > 0;
    const touched = formik.touched[name];
    const meta = {
        touched,
        // Formik validates the whole form on every keystroke, so only surface an
        // error once the field itself was touched or the step was submitted.
        error: touched || submitFailed ? formik.errors[name] : undefined,
        submitFailed,
    };

    return <Component input={input} meta={meta} type={type} value={radioValue} {...rest} />;
};

/**
 * HOC style for use with Formik's <Field />:
 * const MyFieldFormik = withFormikFieldAdapter(MyComp);
 * <Field name="myField" component={MyFieldFormik} ... />
 */
export const withFormikFieldAdapter = (Component) => ({ field, form, ...rest }) => (
    <Component
        input={{
            ...field,
            onChange: (x) => {
                if (x && typeof x === 'object' && x.target !== undefined) {
                    form.setFieldValue(field.name, x.target.value);
                } else {
                    form.setFieldValue(field.name, x);
                }
            },
            onBlur: (e) => {
                form.setFieldTouched(field.name, true);
                if (field.onBlur) field.onBlur(e);
            },
        }}
        meta={{
            touched: form.touched[field.name],
            // Formik validates the whole form on every keystroke, so only surface an
            // error once the field itself was touched or the step was submitted.
            error:
                form.touched[field.name] || form.submitCount > 0
                    ? form.errors[field.name]
                    : undefined,
            submitFailed: form.submitCount > 0,
        }}
        {...rest}
    />
);

export default FormikFieldAdapter;
